import { resolveSeasonFilters } from '@statsbomb/kitbag-global-football/state';
import { atom, Getter } from 'jotai';
import { unwrap } from 'jotai/utils';
import { convertOptionsToValues } from '@/utils/array';
import { getAtomForSection } from '@/utils/atoms/section';
import { playerSeasonOptionsAtom } from '../../queries/players';
import { teamSeasonOptionsAtom } from '../../queries/teams';
import { eventDataSeasonsOptionsAtom } from '../dataLocker/seasons';
import { filtersAtom } from '../filter';

const seasonSectionMapAtom = atom(() => ({
  player: playerSeasonOptionsAtom,
  team: teamSeasonOptionsAtom,
  dataLocker: eventDataSeasonsOptionsAtom,
  game: eventDataSeasonsOptionsAtom,
}));

export const seasonOptionsAtom = atom(getAtomForSection(seasonSectionMapAtom));

const unwrappedSeasonOptionsAtom = unwrap(seasonOptionsAtom, prev => prev || null);

// A failed option load reads as null (the "not loaded" sentinel) rather than
// rethrowing through every downstream filter atom: those atoms also depend on
// the shared filter scope, and a memoised rejection re-thrown from a later
// scope flush would surface as an unhandled error outside React. Reading via
// a synchronous catch (rather than a wrapping async atom) keeps settling on
// the original tick, so components don't re-render outside the test's act().
const readSeasonOptions = (get: Getter) => {
  try {
    return get(unwrappedSeasonOptionsAtom);
  } catch {
    return null;
  }
};

// The resolution rules (defaults, explicit-clear vs untouched, filter-by ids)
// live in kitbag-global-football. Each atom keeps its own minimal dependency
// set (defaults must not depend on the selection state) so a rejection or
// update in one input only flushes through the atoms that genuinely read it.
export const defaultSeasonsAtom = atom(
  get =>
    resolveSeasonFilters({
      selectedSeasonIds: null,
      seasonOptions: readSeasonOptions(get),
      currentYear: new Date().getFullYear(),
    }).defaultSeasons,
);

export const selectedSeasonsAtom = atom(get => {
  const seasonOptions = readSeasonOptions(get);
  return resolveSeasonFilters({
    selectedSeasonIds: get(filtersAtom).seasonIds,
    seasonOptions,
    currentYear: new Date().getFullYear(),
  }).selectedSeasons;
});

export const seasonIdsToFilterByAtom = atom(get => {
  const selectedSeasonIds = get(filtersAtom).seasonIds;
  if (selectedSeasonIds) return selectedSeasonIds;
  return convertOptionsToValues(get(selectedSeasonsAtom));
});

const seasonsLoadedAtom = atom(async get => {
  await get(seasonOptionsAtom);
  return true;
});

export const haveSeasonOptionsLoadedAtom = unwrap(seasonsLoadedAtom, prev => prev || false);
